import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaCircleCheck } from "react-icons/fa6";
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { skipNextStepRedirect, useStepRedirect } from '../../../hooks/useStepRedirect';

const SuccessScreen: React.FC = () => {
  // Doctors sit on this page after submitting. Without the redirect hook, a
  // staff corrections request (changes_requested) was invisible on refresh —
  // the doctor had to sign out/in to be routed to /corrections. In-flow
  // arrivals are unaffected: navigation here calls skipNextStepRedirect().
  useStepRedirect();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  useEffect(() => {
    const timer1 = setTimeout(() => setShowSuccessAnimation(true), 300);
    const timer2 = setTimeout(() => setShowContent(true), 800);
    return () => { clearTimeout(timer1); clearTimeout(timer2); };
  }, []);

  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      centerContent={true}
      title=""
      maxHeight="90vh"
      className="bg-gradient-to-br from-green-50 to-blue-50"
    >
      <div className="w-full flex flex-col items-center justify-center space-y-6 text-center px-4">
        {/* Animated Success Icon */}
        <div className="relative">
          <div
            className={`w-24 h-24 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center shadow-2xl transition-all duration-1000 transform ${
              showSuccessAnimation ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
            }`}
          >
            <FaCheck className="text-4xl text-white" />
          </div>
        </div>

        {/* Title */}
        <div
          className={`transition-all duration-800 delay-500 transform ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <h1 className="text-2xl font-bold text-gray-800">
            {t('application_sent_successfully')}
          </h1>
        </div>

        {/* Checklist */}
        <div
          className={`w-full max-w-md transition-all duration-800 delay-700 transform ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 space-y-3">
            <div className="flex items-center gap-3 text-green-600">
              <FaCircleCheck className="text-lg flex-shrink-0" />
              <span className="text-sm font-medium">{t('success_application_submitted')}</span>
            </div>
            <div className="flex items-center gap-3 text-blue-600">
              <FaCircleCheck className="text-lg flex-shrink-0" />
              <span className="text-sm font-medium">{t('success_all_documents_complete')}</span>
            </div>
            <div className="flex items-center gap-3 text-purple-600">
              <FaCircleCheck className="text-lg flex-shrink-0" />
              <span className="text-sm font-medium">{t('success_fees_processed')}</span>
            </div>
          </div>
        </div>

        {/* Message */}
        <div
          className={`w-full max-w-md transition-all duration-800 delay-900 transform ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <p className={`text-sm text-gray-600 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('inspector_will_contact')}
          </p>
        </div>

        {/* View Requests Button */}
        <div
          className={`w-full max-w-md transition-all duration-800 delay-1100 transform ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
        >
          <button
            onClick={() => {
              skipNextStepRedirect();
              navigate('/requests');
            }}
            className="w-full min-h-[56px] px-6 py-4 rounded-2xl bg-[#2E5D7D] text-white text-base font-semibold hover:opacity-90 transition-all shadow-md"
          >
            {t('view_my_requests')}
          </button>
        </div>
      </div>
    </ResponsiveFormLayout>
  );
};

export default SuccessScreen;
